import { AcceptIncomingCallPublisher } from '@main/publishers/accept-incoming-call.publisher';
import { ConnectedCallPublisher } from '@main/publishers/connected-call.publisher';
import { DialNumberPublisher } from '@main/publishers/dial-number.publisher';
import { DisconnectedCallPublisher } from '@main/publishers/disconnected-call.publisher';
import { HangupCallPublisher } from '@main/publishers/hangup-call.publisher';
import { HoldTogglePublisher } from '@main/publishers/hold-toggle.publisher';
import { IdleCallPublisher } from '@main/publishers/idle-call.publisher';
import { MuteTogglePublisher } from '@main/publishers/mute-toggle.publisher';
import { RejectIncomingCallPublisher } from '@main/publishers/reject-incoming-call.publisher';
import { RingIncomingCallPublisher } from '@main/publishers/ring-incoming-call.publisher';
import { StatusSyncCrmToAircallPublisher } from '@main/publishers/status-sync-crm-to-aircall.publisher';
import { SupervisorListenPublisher } from '@call-monitor/supervisorListening/supervisor-listening.publisher';
import type { SupervisorListenEventPublisher } from '@call-monitor/supervisorListening/supervisor-listening.usecase';
import { SupervisorDisconnectPublisher } from '@call-monitor/supervisorDisconnect/supervisor-disconnect.publisher';
import type { SupervisorDisconnectEventPublisher } from '@call-monitor/supervisorDisconnect/supervisor-disconnect.usecase';
import { UserLoginPublisher } from '@main/publishers/user-login.publisher';
import { UserLogoutPublisher } from '@main/publishers/user-logout.publisher';
import { WrapUpTimeEndedPublisher } from '@main/publishers/wrap-up-time-ended.publisher';
import { WrapUpTimeStartedPublisher } from '@main/publishers/wrap-up-time-started.publisher';
import type { AcceptIncomingCallEventPublisher } from '@usecases/accept-incoming-call.usecase';
import type { ConnectedCallEventPublisher } from '@usecases/connected-call.usecase';
import type { DialNumberEventPublisher } from '@usecases/dial-number.usecase';
import type { DisconnectedCallEventPublisher } from '@usecases/disconnected-call.usecase';
import type { HangupCallEventPublisher } from '@usecases/hangup-call.usecase';
import type { HoldToggleEventPublisher } from '@usecases/hold-toggle.usecase';
import type { IdleCallEventPublisher } from '@usecases/idle-call.usecase';
import type { MuteToggleEventPublisher } from '@usecases/mute-toggle.usecase';
import type { RejectIncomingCallEventPublisher } from '@usecases/reject-incoming-call.usecase';
import type { StatusSyncAircallToCrmEventPublisher } from '@usecases/status-sync-aircall-to-crm.usecase';
import type { StatusSyncCrmToAircallEventPublisher } from '@usecases/status-sync-crm-to-aircall.usecase';
import { FetchPrerecordedMessagesPublisher } from '../../voiceMailDrop/fetch-list-prerecorded-messages.publisher';
import type { FetchPrerecordedMessagesEventPublisher } from '../../voiceMailDrop/fetch-list-prerecorded-messages.usecase';
import { VoiceMailDropPublisher } from '../../voiceMailDrop/voice-mail-drop.publisher';
import type { VoiceMailDropEventPublisher } from '../../voiceMailDrop/voice-mail-drop.usecase';
import { BlindTransferNumberPublisher } from '../../callTransfer/blindTransfer/blind-transfer-call.publisher';
import type { BlindTransferNumberEventPublisher } from '../../callTransfer/blindTransfer/blind-transfer-call.usecase';
import { RecordingTogglePublisher } from '../../call-recording/recording-toggle.publisher';
import type { RecordingToggleEventPublisher } from '../../call-recording/recording-toggle.usecase';
import { ConnectedTransferCallPublisher } from '../../callTransfer/connect-merge-transfer/connect-merge-transfer-call.publisher';
import type { ConnectedTransferCallEventPublisher } from '../../callTransfer/connect-merge-transfer/connect-merge-transfer-call.usecase';
import { DisconnectedTransferCallPublisher } from '../../callTransfer/disconnect-transfer/disconnect-transfer-call.publisher';
import type { DisconnectedTransferCallEventPublisher } from '../../callTransfer/disconnect-transfer/disconnect-transfer-call.usecase';
import { HangupTransferCallPublisher } from '../../callTransfer/hangup-transfer/hangup-transfer-call.publisher';
import type { HangupTransferCallEventPublisher } from '../../callTransfer/hangup-transfer/hangup-transfer-call.usecase';
import { ShowAgentListPublisher } from '../../callTransfer/showAgentList/show-agentlist.publisher';
import { StoreAgentListPublisher } from '../../callTransfer/storeAgentList/store-agentlist.publisher';
import type { StoreAgentListEventPublisher } from '../../callTransfer/storeAgentList/store-agentlist.usecase';
import { SwapCallPublisher } from '../../callTransfer/SwapCall/swap-call.publisher';
import type { SwapCallEventPublisher } from '../../callTransfer/SwapCall/swap-call.usecase';
import { TeammateAvailabilityChangedPublisher } from '../../callTransfer/teammateAvailabilityChanged/teammate-availability-changed.publisher';
import type { TeammateAvailabilityChangedEventPublisher } from '../../callTransfer/teammateAvailabilityChanged/teammate-availability-changed.usecase';
import { WarmMergeTransferPublisher } from '../../callTransfer/warmMergeTransfer/warm-merge-transfer.publisher';
import { TransferNumberPublisher } from '../../callTransfer/warmTransfer/warm-transfer-call.publisher';
import type { TransferNumberEventPublisher } from '../../callTransfer/warmTransfer/warm-transfer-call.usecase';
import { MuteAgentPublisher } from '../../callTransfer/MuteAgent/mute-agent.publisher';
import type { MuteAgentEventPublisher } from '../../callTransfer/MuteAgent/mute-agent.usecase';
import { StatusSyncAircallToCrmPublisher } from '../publishers/status-sync-aircall-to-crm.publisher';

export class PublisherFactory {
  private static ringIncomingCallPublisher: RingIncomingCallPublisher;

  static buildRingIncomingCallPublisher(): RingIncomingCallPublisher {
    if (!this.ringIncomingCallPublisher) {
      this.ringIncomingCallPublisher = new RingIncomingCallPublisher();
    }

    return this.ringIncomingCallPublisher;
  }
  private static WrapUpTimeStartedPublisher: WrapUpTimeStartedPublisher;
  static buildWrapUpTimeStartedPublisher(): WrapUpTimeStartedPublisher {
    if (!this.WrapUpTimeStartedPublisher) {
      this.WrapUpTimeStartedPublisher = new WrapUpTimeStartedPublisher();
    }
    return this.WrapUpTimeStartedPublisher;
  }

  private static WrapUpTimeEndedPublisher: WrapUpTimeEndedPublisher;
  static buildWrapUpTimeEndedPublisher(): WrapUpTimeEndedPublisher {
    if (!this.WrapUpTimeEndedPublisher) {
      this.WrapUpTimeEndedPublisher = new WrapUpTimeEndedPublisher();
    }
    return this.WrapUpTimeEndedPublisher;
  }

  private static userLoginPublisher: UserLoginPublisher;
  static buildUserLoginPublisher(): UserLoginPublisher {
    return (
      this.userLoginPublisher ??
      (this.userLoginPublisher = new UserLoginPublisher())
    );
  }

  private static userLogoutPublisher: UserLogoutPublisher;
  static buildUserLogoutPublisher(): UserLogoutPublisher {
    return (
      this.userLogoutPublisher ??
      (this.userLogoutPublisher = new UserLogoutPublisher())
    );
  }

  private static acceptIncomingCallPublisher: AcceptIncomingCallEventPublisher;
  static buildAcceptIncomingCallPublisher(): AcceptIncomingCallEventPublisher {
    return (
      this.acceptIncomingCallPublisher ??
      (this.acceptIncomingCallPublisher = new AcceptIncomingCallPublisher())
    );
  }

  private static HangupCallPublisher: HangupCallEventPublisher;
  static buildHangupCallPublisher(): HangupCallEventPublisher {
    return (
      this.HangupCallPublisher ??
      (this.HangupCallPublisher = new HangupCallPublisher())
    );
  }

  private static HangupTransferCallPublisher: HangupTransferCallEventPublisher;
  static buildHangupTransferCallPublisher(): HangupTransferCallEventPublisher {
    return (
      this.HangupTransferCallPublisher ??
      (this.HangupTransferCallPublisher = new HangupTransferCallPublisher())
    );
  }

  private static muteTogglePublisher: MuteToggleEventPublisher;
  static buildMuteTogglePublisher(): MuteToggleEventPublisher {
    return (
      this.muteTogglePublisher ??
      (this.muteTogglePublisher = new MuteTogglePublisher())
    );
  }

  private static recordingTogglePublisher: RecordingToggleEventPublisher;
  static buildRecordingTogglePublisher(): RecordingToggleEventPublisher {
    return (
      this.recordingTogglePublisher ??
      (this.recordingTogglePublisher = new RecordingTogglePublisher())
    );
  }

  private static holdTogglePublisher: HoldToggleEventPublisher;
  static buildHoldTogglePublisher(): HoldToggleEventPublisher {
    return (
      this.holdTogglePublisher ??
      (this.holdTogglePublisher = new HoldTogglePublisher())
    );
  }

  private static dialNumberPublisher: DialNumberEventPublisher;
  static buildDialNumberPublisher(): DialNumberEventPublisher {
    return (
      this.dialNumberPublisher ??
      (this.dialNumberPublisher = new DialNumberPublisher())
    );
  }

  private static connectedCallPublisher: ConnectedCallEventPublisher;
  static buildConnectedCallPublisher(): ConnectedCallEventPublisher {
    return (
      this.connectedCallPublisher ??
      (this.connectedCallPublisher = new ConnectedCallPublisher())
    );
  }

  private static connectedTransferCallPublisher: ConnectedTransferCallEventPublisher;
  static buildConnectedTransferCallPublisher(): ConnectedTransferCallEventPublisher {
    return (
      this.connectedTransferCallPublisher ??
      (this.connectedTransferCallPublisher =
        new ConnectedTransferCallPublisher())
    );
  }

  private static disconnectedCallPublisher: DisconnectedCallEventPublisher;
  static buildDisconnectedCallPublisher(): DisconnectedCallEventPublisher {
    return (
      this.disconnectedCallPublisher ??
      (this.disconnectedCallPublisher = new DisconnectedCallPublisher())
    );
  }

  private static disconnectedTransferCallPublisher: DisconnectedTransferCallEventPublisher;
  static buildDisconnectedTransferCallPublisher(): DisconnectedTransferCallEventPublisher {
    return (
      this.disconnectedTransferCallPublisher ??
      (this.disconnectedTransferCallPublisher =
        new DisconnectedTransferCallPublisher())
    );
  }

  private static idleCallPublisher: IdleCallEventPublisher;
  static buildIdleCallPublisher(): IdleCallEventPublisher {
    return (
      this.idleCallPublisher ??
      (this.idleCallPublisher = new IdleCallPublisher())
    );
  }

  private static rejectIncomingCallPublisher: RejectIncomingCallEventPublisher;
  static buildRejectIncomingCallPublisher(): RejectIncomingCallEventPublisher {
    return (
      this.rejectIncomingCallPublisher ??
      (this.rejectIncomingCallPublisher = new RejectIncomingCallPublisher())
    );
  }

  private static statusSyncCrmToAircallPublisher: StatusSyncCrmToAircallEventPublisher;
  static buildStatusSyncCrmToAircallPublisher(): StatusSyncCrmToAircallEventPublisher {
    return (
      this.statusSyncCrmToAircallPublisher ??
      (this.statusSyncCrmToAircallPublisher =
        new StatusSyncCrmToAircallPublisher())
    );
  }

  private static statusSyncAircallToCrmPublisher: StatusSyncAircallToCrmEventPublisher;
  static buildStatusSyncAircallToCrmPublisher(): StatusSyncAircallToCrmEventPublisher {
    return (
      this.statusSyncAircallToCrmPublisher ??
      (this.statusSyncAircallToCrmPublisher =
        new StatusSyncAircallToCrmPublisher())
    );
  }

  private static transferNumberPublisher: TransferNumberEventPublisher;
  static buildTransferNumberPublisher(): TransferNumberEventPublisher {
    return (
      this.transferNumberPublisher ??
      (this.transferNumberPublisher = new TransferNumberPublisher())
    );
  }

  private static blindTransferNumberPublisher: BlindTransferNumberEventPublisher;
  static buildBlindTransferNumberPublisher(): BlindTransferNumberEventPublisher {
    return (
      this.blindTransferNumberPublisher ??
      (this.blindTransferNumberPublisher = new BlindTransferNumberPublisher())
    );
  }

  private static storeAgentListPublisher: StoreAgentListEventPublisher;
  static buildStoreAgentListPublisher(): StoreAgentListEventPublisher {
    return (
      this.storeAgentListPublisher ??
      (this.storeAgentListPublisher = new StoreAgentListPublisher())
    );
  }

  private static showAgentListPublisher: ShowAgentListPublisher;
  static buildShowAgentListPublisher(): ShowAgentListPublisher {
    return (
      this.showAgentListPublisher ??
      (this.showAgentListPublisher = new ShowAgentListPublisher())
    );
  }

  private static teammateAvailabilityChangedPublisher: TeammateAvailabilityChangedEventPublisher;
  static buildTeammateAvailabilityChangedPublisher(): TeammateAvailabilityChangedEventPublisher {
    return (
      this.teammateAvailabilityChangedPublisher ??
      (this.teammateAvailabilityChangedPublisher =
        new TeammateAvailabilityChangedPublisher())
    );
  }

  private static WarmMergeTransferPublisher: WarmMergeTransferPublisher;
  static buildWarmMergeTransferPublisher(): WarmMergeTransferPublisher {
    return (
      this.WarmMergeTransferPublisher ??
      (this.WarmMergeTransferPublisher = new WarmMergeTransferPublisher())
    );
  }

  private static swapCallPublisher: SwapCallEventPublisher;
  static buildSwapCallPublisher(): SwapCallEventPublisher {
    return (
      this.swapCallPublisher ??
      (this.swapCallPublisher = new SwapCallPublisher())
    );
  }

  private static MuteAgentPublisher: MuteAgentEventPublisher;
  static buildMuteAgentPublisher(): MuteAgentEventPublisher {
    return (
      this.MuteAgentPublisher ??
      (this.MuteAgentPublisher = new MuteAgentPublisher())
    );
  }

  private static supervisorListenPublisher: SupervisorListenEventPublisher;
  static buildSupervisorListenPublisher(): SupervisorListenEventPublisher {
    return (
      this.supervisorListenPublisher ??
      (this.supervisorListenPublisher = new SupervisorListenPublisher())
    );
  }

  private static supervisorDisconnectPublisher: SupervisorDisconnectEventPublisher;
  static buildSupervisorDisconnectPublisher(): SupervisorDisconnectEventPublisher {
    return (
      this.supervisorDisconnectPublisher ??
      (this.supervisorDisconnectPublisher = new SupervisorDisconnectPublisher())
    );
  }

  private static FetchPrerecordedMessagesPublisher: FetchPrerecordedMessagesEventPublisher;
  static buildFetchPrerecordedMessagesPublisher(): FetchPrerecordedMessagesEventPublisher {
    return (
      this.FetchPrerecordedMessagesPublisher ??
      (this.FetchPrerecordedMessagesPublisher =
        new FetchPrerecordedMessagesPublisher())
    );
  }

  private static VoiceMailDropPublisher: VoiceMailDropEventPublisher;
  static buildVoiceMailDropPublisher(): VoiceMailDropEventPublisher {
    return (
      this.VoiceMailDropPublisher ??
      (this.VoiceMailDropPublisher = new VoiceMailDropPublisher())
    );
  }
}
